import React, { useState, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const ForgotPassword = memo(() => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { handleAuthError, showSuccess, showWarning } = useErrorHandler();

  const validateEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const trimmedEmail = email.trim().toLowerCase();
    
    // Validação frontend
    if (!trimmedEmail) {
      showWarning('Por favor, insira seu email.');
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      showWarning('Por favor, insira um email válido.');
      return;
    }

    setIsLoading(true);
    
    try {
      // Simular envio de email de recuperação (sem Supabase)
      console.log('Simulando envio de email de recuperação para:', trimmedEmail);
      
      // Delay artificial para simular requisição
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIsEmailSent(true);
      showSuccess('Email de recuperação enviado! Verifique sua caixa de entrada.');
    } catch (error) {
      console.error('Erro ao enviar email de recuperação:', error);
      handleAuthError(error);
    } finally {
      setIsLoading(false);
    }
  }, [email, validateEmail, handleAuthError, showSuccess, showWarning]);

  if (isEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 p-4">
        <Helmet>
          <title>Email Enviado | Media App</title>
        </Helmet>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10 text-center"
        >
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
            className="mx-auto h-16 w-16 bg-green-500/10 rounded-full flex items-center justify-center mb-6"
          >
            <CheckCircle className="h-8 w-8 text-green-500" />
          </motion.div>
          
          <h2 className="text-3xl font-bold text-white mb-4">
            Email Enviado!
          </h2>
          
          <p className="text-slate-400 mb-8">
            Enviamos um link de recuperação para <strong className="text-cyan-400">{email}</strong>.
            <br />
            Verifique sua caixa de entrada e siga as instruções.
          </p>
          
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-6 py-3 border border-slate-700 text-base font-medium rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all w-full"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Voltar para o login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 p-4">
      <Helmet>
        <title>Recuperar Senha | Media App</title>
      </Helmet>
      
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10"
      >
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-4">
            <Mail className="h-6 w-6 text-cyan-400" />
          </div>
          <h2 className="text-3xl font-bold text-white">
            Recuperar Senha
          </h2>
          <p className="mt-2 text-slate-400">
            Digite seu email para receber um link de recuperação
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-slate-500" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full pl-10 pr-3 py-3 border border-slate-700 rounded-lg bg-slate-950 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/20"
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enviando...
              </span>
            ) : (
              'Enviar Link de Recuperação'
            )}
          </button>

          <div className="text-center mt-4">
            <Link
              to="/login"
              className="text-cyan-400 hover:text-cyan-300 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para o login
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
});

ForgotPassword.displayName = 'ForgotPassword';

export default ForgotPassword;